import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  TextField,
  CircularProgress,
  LinearProgress,
  Alert,
  Grid,
  Tabs,
  Tab,
  Autocomplete,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useToast } from '../../hooks/useToast';
import { propertyDisplayNameForApi } from './cashReconPropertyApi';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateUploadOutlinedSx,
  harmonateDataGridSx,
  harmonateFormFieldsSx,
  harmonateTabsSx,
} from '../../../theme/harmonateStyles';

const a11yProps = (index) => ({
  id: `refresh-data-tab-${index}`,
  'aria-controls': `refresh-data-tabpanel-${index}`,
});

/** Postgres sync job poll result — prefer nested `result` for final counts */
const formatPostgresSyncJobCompleteMessage = (st, label) => {
  const r = st?.result && typeof st.result === 'object' ? st.result : st;
  if (!r || typeof r !== 'object') return `${label} Postgres sync completed.`;
  const pr = r.processed_rows ?? st?.processed_rows ?? 0;
  const up = r.upserted_rows ?? st?.upserted_rows ?? 0;
  const est = r.estimated_total_rows ?? st?.estimated_total_rows;
  const rps = r.rows_per_second ?? st?.rows_per_second;
  const el = r.elapsed_seconds ?? st?.elapsed_seconds;
  let extra = `Processed: ${pr} · Upserted: ${up}`;
  if (est != null) extra += ` · Est. total: ${est}`;
  if (el != null) extra += ` · ${Number(el).toFixed(1)}s`;
  if (rps != null) extra += ` · ${Number(rps).toFixed(0)} rows/s`;
  return `${label} → Postgres: ${extra}`;
};

/** Status poll payloads may expose terminal state on `status` or only under `result.status`. */
const getPostgresSyncPollStatusKey = (st) => {
  const raw = st?.status ?? st?.result?.status;
  return String(raw ?? '').trim().toLowerCase();
};

/** Failed jobs store the reason on `error`. `message` and `detail` are often empty. */
const postgresSyncFailureText = (st, fallback) => {
  const nested = st?.result && typeof st.result === 'object' ? st.result : null;
  const raw = st?.error ?? st?.message ?? st?.detail ?? nested?.error ?? nested?.message ?? nested?.detail;
  const text = raw == null ? '' : String(raw).trim();
  return text || fallback;
};

const POSTGRES_SYNC_DONE = new Set(['success', 'completed']);
const POSTGRES_SYNC_STOPPED = new Set(['failed', 'error', 'cancelled']);

/**
 * Progress for a sync-to-postgres job.
 * A finished job whose Snowflake count is 0 must be determinate at 100%.
 * `estimated_total_rows > 0` is false for that case, which otherwise leaves
 * the bar indeterminate and looks like the refresh never finishes.
 */
const getPostgresSyncProgress = (job) => {
  const status = getPostgresSyncPollStatusKey(job);
  const nested = job?.result && typeof job.result === 'object' ? job.result : null;
  const estRaw = job?.estimated_total_rows ?? nested?.estimated_total_rows;
  const processedRaw = job?.processed_rows ?? nested?.processed_rows;
  const est = estRaw == null || estRaw === '' ? null : Number(estRaw);
  const processed = processedRaw == null || processedRaw === '' ? null : Number(processedRaw);
  const estKnown = est != null && Number.isFinite(est);
  const processedKnown = processed != null && Number.isFinite(processed);

  if (POSTGRES_SYNC_DONE.has(status)) {
    if (!estKnown || est <= 0 || !processedKnown) return { variant: 'determinate', value: 100 };
    return { variant: 'determinate', value: Math.min(100, (100 * processed) / est) };
  }

  if (POSTGRES_SYNC_STOPPED.has(status)) {
    if (estKnown && est > 0 && processedKnown) {
      return { variant: 'determinate', value: Math.min(100, (100 * processed) / est) };
    }
    return { variant: 'determinate', value: 0 };
  }

  if (estKnown && est > 0 && processedKnown) {
    return { variant: 'determinate', value: Math.min(100, (100 * processed) / est) };
  }

  return { variant: 'indeterminate', value: 0 };
};

const PostgresSyncJobProgress = ({ job }) => {
  const { variant, value } = getPostgresSyncProgress(job);
  const done = POSTGRES_SYNC_DONE.has(getPostgresSyncPollStatusKey(job));
  return (
    <LinearProgress
      variant={variant}
      value={value}
      sx={{
        height: 6,
        borderRadius: 1,
        ...(done
          ? {
              bgcolor: '#d9eadf',
              '& .MuiLinearProgress-bar': { bgcolor: ht.green },
            }
          : {}),
      }}
    />
  );
};

/** Replaces the raw "success" status so a finished job is obvious next to the full bar. */
const PostgresSyncStatusLabel = ({ job }) => {
  const status = getPostgresSyncPollStatusKey(job);
  if (POSTGRES_SYNC_DONE.has(status)) {
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.4,
          color: ht.green,
          fontWeight: 700,
          verticalAlign: 'middle',
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 16 }} aria-hidden />
        Complete
      </Box>
    );
  }
  return String(job?.status || '—');
};

/** Same auto-hide as the app toast default in useToast. */
const POSTGRES_JOB_SUCCESS_TOAST_MS = 3000;

/** Shared FastAPI *-count endpoints (confirm dialog + grid “Total records”) */
async function fetchFastApiRecordCount(type, ctx) {
  const {
    token,
    tenantId,
    glFromDate,
    glToDate,
    glPropertyId,
    propertyApis,
    bankFromDate,
    bankToDate,
    bankPropertyId,
  } = ctx;
  const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  if (!token) {
    const e = new Error('No authentication token found.');
    e.code = 'NO_TOKEN';
    throw e;
  }
  if (!tenantId) {
    const e = new Error('Tenant ID (tenant_integration) is required.');
    e.code = 'NO_TENANT';
    throw e;
  }

  if (type === 'CHART') {
    const url = `${baseUrl}fastapi/v1/cash-recon/gl-chart-of-accounts-count?tenant_id=${encodeURIComponent(tenantId)}`;
    const resp = await fetch(url, { method: 'GET', headers });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || data.status !== 'success') {
      throw new Error(data.message || data.detail || `Failed to fetch count (${resp.status})`);
    }
    return typeof data.count === 'number' ? data.count : Number(data.count) || 0;
  }

  if (type === 'GL') {
    let url = `${baseUrl}fastapi/v1/cash-recon/gl-transactions-count?tenant_id=${encodeURIComponent(tenantId)}`;
    if (glFromDate) url += `&start_date=${encodeURIComponent(glFromDate)}`;
    if (glToDate) url += `&end_date=${encodeURIComponent(glToDate)}`;
    const glProp = glPropertyId && propertyApis?.length
      ? propertyApis.find((p) => p.propertyId === glPropertyId)
      : null;
    const glPropName = propertyDisplayNameForApi(glProp);
    if (glPropName) url += `&property_name=${encodeURIComponent(glPropName)}`;
    const resp = await fetch(url, { method: 'GET', headers });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || data.status !== 'success') {
      throw new Error(data.message || data.detail || `Failed to fetch count (${resp.status})`);
    }
    return typeof data.count === 'number' ? data.count : Number(data.count) || 0;
  }

  if (type === 'BANK') {
    let url = `${baseUrl}fastapi/v1/cash-recon/bank-normalized-data-count?tenant_id=${encodeURIComponent(tenantId)}`;
    if (bankFromDate) url += `&start_date=${encodeURIComponent(bankFromDate)}`;
    if (bankToDate) url += `&end_date=${encodeURIComponent(bankToDate)}`;
    const bankProp = bankPropertyId && propertyApis?.length
      ? propertyApis.find((p) => p.propertyId === bankPropertyId)
      : null;
    const bankPropName = propertyDisplayNameForApi(bankProp);
    if (bankPropName) url += `&property_name=${encodeURIComponent(bankPropName)}`;
    const resp = await fetch(url, { method: 'GET', headers });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || data.status !== 'success') {
      throw new Error(data.message || data.detail || `Failed to fetch count (${resp.status})`);
    }
    return typeof data.count === 'number' ? data.count : Number(data.count) || 0;
  }

  return 0;
}

const POSTGRES_SYNC_BATCH_SIZE = 10000;

/** `tenant_integration` = FastAPI `tenant_id`; `tenant_id` (LS) = UUID for `tenant_uuid` (matches sync-to-postgres API). */
const getPostgresSyncBaseParams = () => {
  const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003/').replace(/\/+$/, '/');
  const fastTenantId = localStorage.getItem('tenant_integration') || '';
  const tenantUuid = localStorage.getItem('tenant_id') || '';
  return { baseUrl, fastTenantId, tenantUuid };
};

const buildPostgresSyncStartUrl = (pathSegment, { startDate, endDate, propertyName } = {}) => {
  const { baseUrl, fastTenantId, tenantUuid } = getPostgresSyncBaseParams();
  const u = new URL(
    `${baseUrl}fastapi/v1/cash-recon/${pathSegment}/sync-to-postgres/start`,
  );
  if (fastTenantId) u.searchParams.set('tenant_id', fastTenantId);
  if (tenantUuid) u.searchParams.set('tenant_uuid', tenantUuid);
  u.searchParams.set('batch_size', String(POSTGRES_SYNC_BATCH_SIZE));
  if (startDate) u.searchParams.set('start_date', startDate);
  if (endDate) u.searchParams.set('end_date', endDate);
  if (propertyName) u.searchParams.set('property_name', propertyName);
  return u.toString();
};

const buildPostgresSyncStatusUrl = (pathSegment, jobId) => {
  const { baseUrl } = getPostgresSyncBaseParams();
  return `${baseUrl}fastapi/v1/cash-recon/${pathSegment}/sync-to-postgres/status/${encodeURIComponent(jobId)}`;
};

/** CoA sync-to-postgres start endpoint with tenant + batch only (backend derives defaults). */
const buildCoaPostgresSyncStartUrl = () => {
  const { baseUrl, fastTenantId, tenantUuid } = getPostgresSyncBaseParams();
  const u = new URL(
    `${baseUrl}fastapi/v1/cash-recon/gl-chart-of-accounts/sync-to-postgres/start`,
  );
  if (fastTenantId) u.searchParams.set('tenant_id', fastTenantId);
  if (tenantUuid) u.searchParams.set('tenant_uuid', tenantUuid);
  u.searchParams.set('batch_size', String(POSTGRES_SYNC_BATCH_SIZE));
  return u.toString();
};

const buildCoaPostgresSyncStatusUrl = (jobId) =>
  buildPostgresSyncStatusUrl('gl-chart-of-accounts', jobId);

const mapGlChartOfAccountsApiToRows = (items) =>
  (Array.isArray(items) ? items : []).map((item, index) => ({
    id: item.SYSTEM_ID || `chart-${index}`,
    glSystem: item.GL_SYSTEM || '',
    glCode: item.GL_CODE || '',
    glDescription: item.GL_DESCRIPTION || '',
    glAccount: item.GL_ACCOUNT || '',
    glKey: item.GL_KEY || '',
    glAccountType: item.GL_ACCOUNT_TYPE || '',
    glAlternativeAccount: item.GL_ALTERNATIVE_GL_ACCOUNT || '',
    glAuditCreated: item.GL_AUDIT_CREATEDDATETIME || '',
    glAuditModified: item.GL_AUDIT_MODIFIEDDATETIME || '',
    glCategory: item.GL_CATEGORY || '',
    glCloseToAccountId: item.GL_CLOSETOGLACCOUNT_ID || '',
    glCloseToAccountKey: item.GL_CLOSETOGLACCOUNT_KEY || '',
    glClosingType: item.GL_CLOSINGTYPE || '',
    glDisallowDirectPosting: item.GL_DISALLOWDIRECTPOSTING,
    glEntityId: item.GL_ENTITY_ID || '',
    glEntityKey: item.GL_ENTITY_KEY || '',
    glEntityName: item.GL_ENTITY_NAME || '',
    glIsTaxable: item.GL_ISTAXABLE,
    glMrcCode: item.GL_MRCCODE || '',
    glNormalBalance: item.GL_NORMALBALANCE || '',
    glStatus: item.GL_STATUS || '',
    glTaxCode: item.GL_TAXCODE || '',
    originalData: item,
  }));

/** Same minimum tab body height and preview grid height across GL, Bank, Chart, and Postgres tabs. */
const REFRESH_DATA_TAB_PANEL_MIN_HEIGHT = 600;
const REFRESH_DATA_GRID_HEIGHT = 400;

const CustomTabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`refresh-data-tabpanel-${index}`}
    aria-labelledby={`refresh-data-tab-${index}`}
    {...other}
  >
    {value === index && (
      <Box
        sx={{
          minHeight: REFRESH_DATA_TAB_PANEL_MIN_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'visible',
          pt: 0.5,
        }}
      >
        {children}
      </Box>
    )}
  </div>
);

/**
 * RefreshData.jsx
 * Standalone dialog: defaults to the combined Bank + GL + Chart (Postgres) view; optional GL / Bank / Chart tabs from the title action.
 * GL, Bank, and Chart tabs use FastAPI `sync-to-postgres/start` + `sync-to-postgres/status` for Load.
 * The combined tab shows bank + GL + chart totals (chart count is tenant-wide) and starts all three sync jobs from the footer.
 * Optional onBankSaved / onGLSaved callbacks for parent refresh (e.g. when embedded in Mapping).
 */
const RefreshData = ({
  open,
  onClose,
  onBankSaved,
  onGLSaved,
  showSuccess: showSuccessProp,
  showError: showErrorProp,
  showToast: showToastProp,
}) => {
  const internalToast = useToast();
  const showSuccess = showSuccessProp ?? internalToast.showSuccess;
  const showError = showErrorProp ?? internalToast.showError;
  const showToast = showToastProp ?? internalToast.showToast;

  /** 0 = GL, 1 = Bank, 2 = Chart, 3 = combined Bank & GL & Chart (Postgres). Default combined; individual tabs are opt-in. */
  const [activeTab, setActiveTab] = useState(3);
  const [showIndividualRefreshTabs, setShowIndividualRefreshTabs] = useState(false);

  // Shared: properties (GL + Bank tabs)
  const [propertyApis, setPropertyApis] = useState([]);
  const [loadingPropertyApis, setLoadingPropertyApis] = useState(false);

  // Bank Refresh Data state
  const [bankPropertyId, setBankPropertyId] = useState('');
  const [bankFromDate, setBankFromDate] = useState('');
  const [bankToDate, setBankToDate] = useState('');
  const [bankData, setBankData] = useState([]);
  const [bankHasMore, setBankHasMore] = useState(false);
  const [bankTotalCount, setBankTotalCount] = useState(0);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankError, setBankError] = useState(null);
  const [bankSaving, setBankSaving] = useState(false);
  const [bankSaveError, setBankSaveError] = useState(null);
  const [bankTabPostgresJobStatus, setBankTabPostgresJobStatus] = useState(null);
  const [bankTabPostgresJobError, setBankTabPostgresJobError] = useState(null);
  const [bankTabPostgresSyncStarting, setBankTabPostgresSyncStarting] = useState(false);
  const [bankTabPostgresPolling, setBankTabPostgresPolling] = useState(false);
  const bankTabPostgresPollRef = useRef(null);

  // GL Refresh Data state
  const [glPropertyId, setGlPropertyId] = useState('');
  const [glFromDate, setGlFromDate] = useState('');
  const [glToDate, setGlToDate] = useState('');
  const [glData, setGlData] = useState([]);
  const [glHasMore, setGlHasMore] = useState(false);
  const [glTotalCount, setGlTotalCount] = useState(0);

  // Chart of Accounts Refresh Data state
  const [chartData, setChartData] = useState([]);
  const [chartHasMore, setChartHasMore] = useState(false);
  const [chartTotalCount, setChartTotalCount] = useState(0);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(null);
  const [chartSaving, setChartSaving] = useState(false);
  const [chartPostgresJobStatus, setChartPostgresJobStatus] = useState(null);
  const [chartPostgresJobError, setChartPostgresJobError] = useState(null);
  const [chartPostgresSyncStarting, setChartPostgresSyncStarting] = useState(false);
  const [chartPostgresPolling, setChartPostgresPolling] = useState(false);
  const chartPostgresPollRef = useRef(null);

  // Confirm Load dialog state
  const [confirmLoadOpen, setConfirmLoadOpen] = useState(false);
  const [confirmLoadType, setConfirmLoadType] = useState(null); // 'GL' | 'BANK' | 'CHART'
  /** Record count from FastAPI *-count endpoints (confirm dialog) */
  const [confirmLoadCount, setConfirmLoadCount] = useState(null);
  const [confirmCountLoading, setConfirmCountLoading] = useState(false);
  const [confirmCountError, setConfirmCountError] = useState(null);
  const [glLoading, setGlLoading] = useState(false);
  const [glError, setGlError] = useState(null);
  const [glSaving, setGlSaving] = useState(false);
  const [glSaveError, setGlSaveError] = useState(null);
  const [glTabPostgresJobStatus, setGlTabPostgresJobStatus] = useState(null);
  const [glTabPostgresJobError, setGlTabPostgresJobError] = useState(null);
  const [glTabPostgresSyncStarting, setGlTabPostgresSyncStarting] = useState(false);
  const [glTabPostgresPolling, setGlTabPostgresPolling] = useState(false);
  const glTabPostgresPollRef = useRef(null);
  const [confirmLoadSubmitting, setConfirmLoadSubmitting] = useState(false);

  /** Snowflake totals below grid (same *-count APIs as confirm dialog) */
  const [glApiTotalCount, setGlApiTotalCount] = useState(null);
  const [glApiTotalLoading, setGlApiTotalLoading] = useState(false);
  const [bankApiTotalCount, setBankApiTotalCount] = useState(null);
  const [bankApiTotalLoading, setBankApiTotalLoading] = useState(false);
  const [chartApiTotalCount, setChartApiTotalCount] = useState(null);
  const [chartApiTotalLoading, setChartApiTotalLoading] = useState(false);

  // —— Bank & GL (Postgres) tab: shared filters, Snowflake *-count totals, dual sync-to-postgres jobs + polling
  const [pgPropertyId, setPgPropertyId] = useState('');
  const [pgFromDate, setPgFromDate] = useState('');
  const [pgToDate, setPgToDate] = useState('');
  const [pgApiGlCount, setPgApiGlCount] = useState(null);
  const [pgApiGlCountLoading, setPgApiGlCountLoading] = useState(false);
  const [pgApiBankCount, setPgApiBankCount] = useState(null);
  const [pgApiBankCountLoading, setPgApiBankCountLoading] = useState(false);
  const [pgBankJobStatus, setPgBankJobStatus] = useState(null);
  const [pgBankJobError, setPgBankJobError] = useState(null);
  const [pgBankSyncStarting, setPgBankSyncStarting] = useState(false);
  const [pgGlJobStatus, setPgGlJobStatus] = useState(null);
  const [pgGlJobError, setPgGlJobError] = useState(null);
  const [pgGlSyncStarting, setPgGlSyncStarting] = useState(false);
  const [pgBankPolling, setPgBankPolling] = useState(false);
  const [pgGlPolling, setPgGlPolling] = useState(false);
  const [pgApiChartCount, setPgApiChartCount] = useState(null);
  const [pgApiChartCountLoading, setPgApiChartCountLoading] = useState(false);
  const [pgChartJobStatus, setPgChartJobStatus] = useState(null);
  const [pgChartJobError, setPgChartJobError] = useState(null);
  const [pgChartSyncStarting, setPgChartSyncStarting] = useState(false);
  const [pgChartPolling, setPgChartPolling] = useState(false);
  const bankPollRef = useRef(null);
  const glPollRef = useRef(null);
  const pgChartPollRef = useRef(null);
  const combinedLoadRequestedRef = useRef(false);

  const countCtx = () => ({
    token: localStorage.getItem('token'),
    tenantId: localStorage.getItem('tenant_integration'),
    glFromDate,
    glToDate,
    glPropertyId,
    propertyApis,
    bankFromDate,
    bankToDate,
    bankPropertyId,
  });

  const fetchProperties = async () => {
    setLoadingPropertyApis(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/user-tenant-properties?tenant_id=${tenantId}&user_id=${localStorage.getItem('user_id')}`;
      const resp = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!resp.ok) throw new Error(`Failed to fetch properties: ${resp.status} ${resp.statusText}`);
      const data = await resp.json();
      if (data && Array.isArray(data)) {
        setPropertyApis(data.map((item, index) => ({
          id: item.propertyId || index + 1,
          propertyId: item.propertyId || '',
          propertyName: item.propertyName || '',
          propertyCode: item.propertyCode || '',
        })));
      } else {
        setPropertyApis([]);
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
      setPropertyApis([]);
    } finally {
      setLoadingPropertyApis(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchProperties();
    }
  }, [open]);

  useEffect(() => {
    if (!open || activeTab !== 0) return undefined;
    let cancelled = false;
    setGlApiTotalLoading(true);
    fetchFastApiRecordCount('GL', countCtx())
      .then((c) => {
        if (!cancelled) setGlApiTotalCount(c);
      })
      .catch(() => {
        if (!cancelled) setGlApiTotalCount(null);
      })
      .finally(() => {
        if (!cancelled) setGlApiTotalLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, activeTab, glFromDate, glToDate, glPropertyId, propertyApis]);

  useEffect(() => {
    if (!open || activeTab !== 1) return undefined;
    let cancelled = false;
    setBankApiTotalLoading(true);
    fetchFastApiRecordCount('BANK', countCtx())
      .then((c) => {
        if (!cancelled) setBankApiTotalCount(c);
      })
      .catch(() => {
        if (!cancelled) setBankApiTotalCount(null);
      })
      .finally(() => {
        if (!cancelled) setBankApiTotalLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, activeTab, bankFromDate, bankToDate, bankPropertyId, propertyApis]);

  useEffect(() => {
    if (!open || activeTab !== 2) return undefined;
    let cancelled = false;
    setChartApiTotalLoading(true);
    fetchFastApiRecordCount('CHART', countCtx())
      .then((c) => {
        if (!cancelled) setChartApiTotalCount(c);
      })
      .catch(() => {
        if (!cancelled) setChartApiTotalCount(null);
      })
      .finally(() => {
        if (!cancelled) setChartApiTotalLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, activeTab]);

  useEffect(() => {
    if (!open || activeTab !== 3) return undefined;
    const ctx = {
      ...countCtx(),
      glFromDate: pgFromDate,
      glToDate: pgToDate,
      glPropertyId: pgPropertyId,
    };
    let cancelled = false;
    setPgApiGlCountLoading(true);
    fetchFastApiRecordCount('GL', ctx)
      .then((c) => {
        if (!cancelled) setPgApiGlCount(c);
      })
      .catch(() => {
        if (!cancelled) setPgApiGlCount(null);
      })
      .finally(() => {
        if (!cancelled) setPgApiGlCountLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, activeTab, pgFromDate, pgToDate, pgPropertyId, propertyApis]);

  useEffect(() => {
    if (!open || activeTab !== 3) return undefined;
    const ctx = {
      ...countCtx(),
      bankFromDate: pgFromDate,
      bankToDate: pgToDate,
      bankPropertyId: pgPropertyId,
    };
    let cancelled = false;
    setPgApiBankCountLoading(true);
    fetchFastApiRecordCount('BANK', ctx)
      .then((c) => {
        if (!cancelled) setPgApiBankCount(c);
      })
      .catch(() => {
        if (!cancelled) setPgApiBankCount(null);
      })
      .finally(() => {
        if (!cancelled) setPgApiBankCountLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, activeTab, pgFromDate, pgToDate, pgPropertyId, propertyApis]);

  useEffect(() => {
    if (!open || activeTab !== 3) return undefined;
    let cancelled = false;
    setPgApiChartCountLoading(true);
    fetchFastApiRecordCount('CHART', countCtx())
      .then((c) => {
        if (!cancelled) setPgApiChartCount(c);
      })
      .catch(() => {
        if (!cancelled) setPgApiChartCount(null);
      })
      .finally(() => {
        if (!cancelled) setPgApiChartCountLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, activeTab]);

  const stopPostgresBankPoll = () => {
    if (bankPollRef.current) {
      clearInterval(bankPollRef.current);
      bankPollRef.current = null;
    }
    setPgBankPolling(false);
  };

  const stopPostgresGlPoll = () => {
    if (glPollRef.current) {
      clearInterval(glPollRef.current);
      glPollRef.current = null;
    }
    setPgGlPolling(false);
  };

  const stopPgChartPoll = () => {
    if (pgChartPollRef.current) {
      clearInterval(pgChartPollRef.current);
      pgChartPollRef.current = null;
    }
    setPgChartPolling(false);
  };

  const stopChartPostgresPoll = () => {
    if (chartPostgresPollRef.current) {
      clearInterval(chartPostgresPollRef.current);
      chartPostgresPollRef.current = null;
    }
    setChartPostgresPolling(false);
  };

  const stopBankTabPostgresPoll = () => {
    if (bankTabPostgresPollRef.current) {
      clearInterval(bankTabPostgresPollRef.current);
      bankTabPostgresPollRef.current = null;
    }
    setBankTabPostgresPolling(false);
  };

  const stopGlTabPostgresPoll = () => {
    if (glTabPostgresPollRef.current) {
      clearInterval(glTabPostgresPollRef.current);
      glTabPostgresPollRef.current = null;
    }
    setGlTabPostgresPolling(false);
  };

  const resetOnClose = () => {
    setActiveTab(3);
    setShowIndividualRefreshTabs(false);
    stopPostgresBankPoll();
    stopPostgresGlPoll();
    stopPgChartPoll();
    stopChartPostgresPoll();
    stopBankTabPostgresPoll();
    stopGlTabPostgresPoll();
    setBankPropertyId('');
    setBankFromDate('');
    setBankToDate('');
    setBankData([]);
    setBankHasMore(false);
    setBankTotalCount(0);
    setBankError(null);
    setBankSaveError(null);
    setBankTabPostgresJobStatus(null);
    setBankTabPostgresJobError(null);
    setBankTabPostgresSyncStarting(false);
    setBankTabPostgresPolling(false);
    setGlPropertyId('');
    setGlFromDate('');
    setGlToDate('');
    setGlData([]);
    setGlHasMore(false);
    setGlTotalCount(0);
    setGlError(null);
    setGlSaveError(null);
    setGlTabPostgresJobStatus(null);
    setGlTabPostgresJobError(null);
    setGlTabPostgresSyncStarting(false);
    setGlTabPostgresPolling(false);

    setChartData([]);
    setChartHasMore(false);
    setChartTotalCount(0);
    setChartLoading(false);
    setChartError(null);
    setChartSaving(false);
    setChartPostgresJobStatus(null);
    setChartPostgresJobError(null);
    setChartPostgresSyncStarting(false);
    setChartPostgresPolling(false);

    setConfirmLoadOpen(false);
    setConfirmLoadType(null);
    setConfirmLoadSubmitting(false);
    setConfirmLoadCount(null);
    setConfirmCountLoading(false);
    setConfirmCountError(null);
    setGlApiTotalCount(null);
    setGlApiTotalLoading(false);
    setBankApiTotalCount(null);
    setBankApiTotalLoading(false);
    setChartApiTotalCount(null);
    setChartApiTotalLoading(false);
    setPgPropertyId('');
    setPgFromDate('');
    setPgToDate('');
    setPgApiGlCount(null);
    setPgApiGlCountLoading(false);
    setPgApiBankCount(null);
    setPgApiBankCountLoading(false);
    setPgApiChartCount(null);
    setPgApiChartCountLoading(false);
    setPgBankJobStatus(null);
    setPgBankJobError(null);
    setPgBankSyncStarting(false);
    setPgGlJobStatus(null);
    setPgGlJobError(null);
    setPgGlSyncStarting(false);
    setPgChartJobStatus(null);
    setPgChartJobError(null);
    setPgChartSyncStarting(false);
    setPgBankPolling(false);
    setPgGlPolling(false);
    setPgChartPolling(false);
    combinedLoadRequestedRef.current = false;
  };

  const handleClose = () => {
    resetOnClose();
    onClose?.();
  };

  const fetchConfirmLoadCount = async (type) => {
    setConfirmCountLoading(true);
    setConfirmCountError(null);
    setConfirmLoadCount(null);
    try {
      const count = await fetchFastApiRecordCount(type, countCtx());
      setConfirmLoadCount(count);
    } catch (e) {
      setConfirmCountError(e.message || 'Failed to fetch record count');
    } finally {
      setConfirmCountLoading(false);
    }
  };

  const handleOpenConfirmLoad = (type) => {
    setConfirmLoadType(type);
    setConfirmLoadOpen(true);
    fetchConfirmLoadCount(type);
  };

  const handleCancelConfirmLoad = () => {
    if (confirmLoadSubmitting) return;
    setConfirmLoadOpen(false);
    setConfirmLoadType(null);
    setConfirmLoadCount(null);
    setConfirmCountLoading(false);
    setConfirmCountError(null);
  };

  const handleConfirmLoad = async () => {
    if (confirmLoadSubmitting) return;
    setConfirmLoadSubmitting(true);
    try {
      if (confirmLoadType === 'GL') {
        await handleSaveGLRefreshData();
      } else if (confirmLoadType === 'BANK') {
        await handleSaveBankRefreshData();
      } else if (confirmLoadType === 'CHART') {
        await handleSaveChartRefreshData();
      }
    } finally {
      setConfirmLoadSubmitting(false);
      setConfirmLoadOpen(false);
      setConfirmLoadType(null);
      setConfirmLoadCount(null);
      setConfirmCountLoading(false);
      setConfirmCountError(null);
    }
  };

  const syncInProgress =
    glSaving ||
    bankSaving ||
    chartSaving ||
    confirmLoadSubmitting ||
    chartPostgresSyncStarting ||
    chartPostgresPolling ||
    bankTabPostgresSyncStarting ||
    bankTabPostgresPolling ||
    glTabPostgresSyncStarting ||
    glTabPostgresPolling ||
    pgBankSyncStarting ||
    pgGlSyncStarting ||
    pgChartSyncStarting ||
    pgBankPolling ||
    pgGlPolling ||
    pgChartPolling;

  // —— Bank Refresh Data: GET (paginated; only tenant_id mandatory) ——
  // loadMore: true = fetch next page and append; false = fetch from start (offset 0)
  const fetchBankRefreshData = async (loadMore = false) => {
    const tenantId = localStorage.getItem('tenant_integration');
    if (!tenantId) {
      setBankData([]);
      setBankError('Tenant ID (tenant_integration) is required to load data.');
      return;
    }
    const offset = loadMore ? bankData.length : 0;
    setBankLoading(true);
    if (!loadMore) setBankError(null);
    setBankSaveError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      let apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/cash-recon/bank-normalized-data-view-import?tenant_id=${tenantId}&limit=1000&offset=${offset}`;
      if (bankFromDate) apiUrl += `&start_date=${bankFromDate}`;
      if (bankToDate) apiUrl += `&end_date=${bankToDate}`;
      const selectedProperty = bankPropertyId ? propertyApis.find((p) => p.propertyId === bankPropertyId) : null;
      const bankNameForApi = propertyDisplayNameForApi(selectedProperty);
      if (bankNameForApi) {
        apiUrl += `&property_name=${encodeURIComponent(bankNameForApi)}`;
      }
      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!resp.ok) {
        if (!loadMore) setBankData([]);
        setBankError(`Failed to load data: ${resp.status} ${resp.statusText}`);
        return;
      }
      const data = await resp.json();
      if (data.status === 'success' && Array.isArray(data.data)) {
        const hasMore = data.pagination?.has_more === true;
        const totalCount = data.pagination?.total_count ?? data.pagination?.total ?? data.count ?? 0;
        setBankHasMore(hasMore);
        setBankTotalCount(typeof totalCount === 'number' ? totalCount : 0);
        const processed = data.data.map((item, index) => ({
          id: item.CASH_RECON_ID || `bank-${offset + index}`,
          description: item.BANK_DESCRIPTION || item.NEW_CONTENT?.BANK_DESCRIPTION || '',
          date: item.BANK_TRANSACTION_DATE || item.NEW_CONTENT?.BANK_TRANSACTION_DATE || '',
          docNo: item.BANK_DOCUMENT_NO || item.NEW_CONTENT?.BANK_DOCUMENT_NO || item.BANK_REFERENCE || item.NEW_CONTENT?.BANK_REFERENCE || '',
          debit: Math.abs(item.BANK_DEBITS || item.NEW_CONTENT?.BANK_DEBITS || 0),
          credit: Math.abs(item.BANK_CREDITS || item.NEW_CONTENT?.BANK_CREDITS || 0),
          originalData: item,
        }));
        if (loadMore) {
          setBankData((prev) => [...prev, ...processed]);
        } else if (processed.length > 0) {
          setBankData(processed);
        } else {
          setBankData([]);
        }
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (err) {
      setBankError(err.message || 'Failed to fetch data');
      if (!loadMore) setBankData([]);
      showError(err.message || 'Failed to fetch data');
    } finally {
      setBankLoading(false);
    }
  };

  // —— Bank tab: background Snowflake → Postgres (`bank-transactions/sync-to-postgres`) ——
  const startBankTabPostgresSync = async () => {
    if (bankTabPostgresSyncStarting) return;
    if (!localStorage.getItem('tenant_integration') || !localStorage.getItem('tenant_id')) {
      showError('Tenant context is required (tenant_id + tenant_integration in local storage).');
      return;
    }
    stopBankTabPostgresPoll();
    setBankTabPostgresJobError(null);
    setBankTabPostgresJobStatus(null);
    setBankTabPostgresSyncStarting(true);
    const selectedProperty = bankPropertyId ? propertyApis.find((p) => p.propertyId === bankPropertyId) : null;
    const propertyName = propertyDisplayNameForApi(selectedProperty) ?? undefined;
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const url = buildPostgresSyncStartUrl('bank-transactions', {
        startDate: bankFromDate || undefined,
        endDate: bankToDate || undefined,
        propertyName,
      });
      const resp = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.message || data.detail || `Start failed (${resp.status})`);
      }
      const jobId = data.job_id;
      if (!jobId) throw new Error('No job_id returned from server.');

      let bankTabCompletionToastSent = false;
      setBankTabPostgresPolling(true);

      const runOnce = async () => {
        const stUrl = buildPostgresSyncStatusUrl('bank-transactions', jobId);
        const stResp = await fetch(stUrl, { headers: { Authorization: `Bearer ${token}` } });
        const st = await stResp.json().catch(() => ({}));
        if (!stResp.ok) {
          stopBankTabPostgresPoll();
          setBankTabPostgresJobError(postgresSyncFailureText(st, `Status ${stResp.status}`));
          return true;
        }
        setBankTabPostgresJobStatus(st);
        const s = getPostgresSyncPollStatusKey(st);
        if (s === 'success' || s === 'completed' || s === 'failed' || s === 'error' || s === 'cancelled') {
          stopBankTabPostgresPoll();
          if (s === 'success' || s === 'completed') {
            if (!bankTabCompletionToastSent) {
              bankTabCompletionToastSent = true;
              showToast({
                title: 'Bank → Postgres sync complete',
                message: formatPostgresSyncJobCompleteMessage(st, 'Bank'),
                severity: 'success',
                autoHideDuration: POSTGRES_JOB_SUCCESS_TOAST_MS,
              });
            }
            setBankData([]);
            if (typeof onBankSaved === 'function') await onBankSaved();
          } else {
            const bankTabFail = postgresSyncFailureText(st, 'Bank → Postgres sync did not complete successfully.');
            setBankTabPostgresJobError(bankTabFail);
            showError(bankTabFail);
          }
          return true;
        }
        return false;
      };

      const firstDone = await runOnce();
      if (!firstDone) {
        bankTabPostgresPollRef.current = setInterval(() => {
          void runOnce();
        }, 2000);
      }
    } catch (e) {
      setBankTabPostgresJobError(e.message);
      showError(e.message);
      stopBankTabPostgresPoll();
    } finally {
      setBankTabPostgresSyncStarting(false);
    }
  };

  const handleSaveBankRefreshData = async () => {
    if (!localStorage.getItem('tenant_id') || !localStorage.getItem('tenant_integration')) {
      setBankSaveError('Tenant context not found.');
      showError('Tenant context not found.');
      return;
    }
    setBankSaving(true);
    setBankSaveError(null);
    try {
      await startBankTabPostgresSync();
    } catch (apiError) {
      setBankSaveError(apiError.message || 'Failed to start sync');
      showError(apiError.message || 'Failed to start sync');
    } finally {
      setBankSaving(false);
    }
  };

  // —— GL Refresh Data: GET (paginated; only tenant_id mandatory) ——
  // loadMore: true = fetch next page and append; false = fetch from start (offset 0)
  const fetchGLRefreshData = async (loadMore = false) => {
    const tenantId = localStorage.getItem('tenant_integration');
    if (!tenantId) {
      setGlData([]);
      setGlError('Tenant ID (tenant_integration) is required to load data.');
      return;
    }
    const offset = loadMore ? glData.length : 0;
    setGlLoading(true);
    if (!loadMore) setGlError(null);
    setGlSaveError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      let apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/cash-recon/gl-transactions?tenant_id=${tenantId}&limit=1000&offset=${offset}`;
      if (glFromDate) apiUrl += `&start_date=${glFromDate}`;
      if (glToDate) apiUrl += `&end_date=${glToDate}`;
      const selectedProperty = glPropertyId ? propertyApis.find((p) => p.propertyId === glPropertyId) : null;
      const glNameForApi = propertyDisplayNameForApi(selectedProperty);
      if (glNameForApi) {
        apiUrl += `&property_name=${encodeURIComponent(glNameForApi)}`;
      }
      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!resp.ok) {
        if (!loadMore) setGlData([]);
        setGlError(`Failed to load data: ${resp.status} ${resp.statusText}`);
        return;
      }
      const data = await resp.json();
      if (data.status === 'success' && Array.isArray(data.data)) {
        const hasMore = data.pagination?.has_more === true;
        const totalCount = data.pagination?.total_count ?? data.pagination?.total ?? data.count ?? 0;
        setGlHasMore(hasMore);
        setGlTotalCount(typeof totalCount === 'number' ? totalCount : 0);
        const processed = data.data.map((item, index) => {
          const content = item.CONTENT || {};
          const newContent = item.NEW_CONTENT || {};
          const description = item.TRANSACTION_DESCRIPTION || newContent.TRANSACTION_DESCRIPTION || newContent.TRANSACTION_MEMO || content.description || '';
          const date = item.POSTING_DATE || newContent.POSTING_DATE || content.entryDate || '';
          const docNo = item.DOCUMENT_NUMBER || item.REFERENCE_NUMBER || newContent.DOCUMENT_NUMBER || newContent.REFERENCE_NUMBER || content['journalEntry.txnNumber'] || content['journalEntry.key'] || '';
          const debit = item.DEBIT ?? newContent.DEBIT ?? (content.txnType === 'debit' ? Number(content.txnAmount) : 0) ?? 0;
          const credit = item.CREDIT ?? newContent.CREDIT ?? newContent.AMOUNT ?? (content.txnType === 'credit' ? Number(content.txnAmount) : 0) ?? 0;
          const glDesc = item.GL_DESCRIPTION || content['glAccount.name'] || '';
          const glCode = item.GL_CODE || content['glAccount.id'] || '';
          const rowId = item.GL_SYSTEM_ID || newContent.GL_SYSTEM_ID || content.id || content.key || `gl-${offset + index}`;
          const glSystemTransactionId = item.GL_SYSTEM_TRANSACTION_ID || newContent.GL_SYSTEM_TRANSACTION_ID || rowId;
          return {
            id: rowId,
            glSystemTransactionId,
            description,
            date,
            docNo,
            debit: Number(debit) || 0,
            credit: Number(credit) || 0,
            remarks: `Txn Date: ${date} | Doc_No: ${docNo} | Desc: ${description} | Debits: ${Number(debit) || 0} | Credits: ${Number(credit) || 0}`,
            glDescription: glDesc,
            glCode,
            originalData: item,
            isApiData: true,
          };
        });
        if (loadMore) {
          setGlData((prev) => [...prev, ...processed]);
        } else if (processed.length > 0) {
          setGlData(processed);
        } else {
          setGlData([]);
        }
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (err) {
      setGlError(err.message || 'Failed to fetch data');
      if (!loadMore) setGlData([]);
      showError(err.message || 'Failed to fetch data');
    } finally {
      setGlLoading(false);
    }
  };

  // —— Chart of Accounts Refresh Data: GET (no filters; only tenant_id mandatory) ——
  const fetchChartRefreshData = async () => {
    const tenantId = localStorage.getItem('tenant_integration');
    if (!tenantId) {
      setChartData([]);
      setChartError('Tenant ID (tenant_integration) is required to load data.');
      return;
    }
    setChartLoading(true);
    setChartError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/cash-recon/gl-chart-of-accounts?tenant_id=${tenantId}`;
      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!resp.ok) {
        setChartData([]);
        setChartError(`Failed to load data: ${resp.status} ${resp.statusText}`);
        return;
      }
      const data = await resp.json();
      if (data.status === 'success' && Array.isArray(data.data)) {
        const totalCount = data.count ?? 0;
        setChartTotalCount(typeof totalCount === 'number' ? totalCount : 0);
        const processed = mapGlChartOfAccountsApiToRows(data.data);
        setChartData(processed.length > 0 ? processed : []);
        setChartHasMore(false);
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (err) {
      setChartError(err.message || 'Failed to fetch data');
      setChartData([]);
      showError(err.message || 'Failed to fetch data');
    } finally {
      setChartLoading(false);
    }
  };

  // —— GL tab: background Snowflake → Postgres (`gl-transactions/sync-to-postgres`) ——
  const startGlTabPostgresSync = async () => {
    if (glTabPostgresSyncStarting) return;
    if (!localStorage.getItem('tenant_integration') || !localStorage.getItem('tenant_id')) {
      showError('Tenant context is required (tenant_id + tenant_integration in local storage).');
      return;
    }
    stopGlTabPostgresPoll();
    setGlTabPostgresJobError(null);
    setGlTabPostgresJobStatus(null);
    setGlTabPostgresSyncStarting(true);
    const selectedProperty = glPropertyId ? propertyApis.find((p) => p.propertyId === glPropertyId) : null;
    const propertyName = propertyDisplayNameForApi(selectedProperty) ?? undefined;
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const url = buildPostgresSyncStartUrl('gl-transactions', {
        startDate: glFromDate || undefined,
        endDate: glToDate || undefined,
        propertyName,
      });
      const resp = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.message || data.detail || `Start failed (${resp.status})`);
      }
      const jobId = data.job_id;
      if (!jobId) throw new Error('No job_id returned from server.');

      let glTabCompletionToastSent = false;
      setGlTabPostgresPolling(true);

      const runOnce = async () => {
        const stUrl = buildPostgresSyncStatusUrl('gl-transactions', jobId);
        const stResp = await fetch(stUrl, { headers: { Authorization: `Bearer ${token}` } });
        const st = await stResp.json().catch(() => ({}));
        if (!stResp.ok) {
          stopGlTabPostgresPoll();
          setGlTabPostgresJobError(postgresSyncFailureText(st, `Status ${stResp.status}`));
          return true;
        }
        setGlTabPostgresJobStatus(st);
        const s = getPostgresSyncPollStatusKey(st);
        if (s === 'success' || s === 'completed' || s === 'failed' || s === 'error' || s === 'cancelled') {
          stopGlTabPostgresPoll();
          if (s === 'success' || s === 'completed') {
            if (!glTabCompletionToastSent) {
              glTabCompletionToastSent = true;
              showToast({
                title: 'GL → Postgres sync complete',
                message: formatPostgresSyncJobCompleteMessage(st, 'GL'),
                severity: 'success',
                autoHideDuration: POSTGRES_JOB_SUCCESS_TOAST_MS,
              });
            }
            setGlData([]);
            if (typeof onGLSaved === 'function') await onGLSaved();
          } else {
            const glTabFail = postgresSyncFailureText(st, 'GL → Postgres sync did not complete successfully.');
            setGlTabPostgresJobError(glTabFail);
            showError(glTabFail);
          }
          return true;
        }
        return false;
      };

      const firstDone = await runOnce();
      if (!firstDone) {
        glTabPostgresPollRef.current = setInterval(() => {
          void runOnce();
        }, 2000);
      }
    } catch (e) {
      setGlTabPostgresJobError(e.message);
      showError(e.message);
      stopGlTabPostgresPoll();
    } finally {
      setGlTabPostgresSyncStarting(false);
    }
  };

  const handleSaveGLRefreshData = async () => {
    if (!localStorage.getItem('tenant_id') || !localStorage.getItem('tenant_integration')) {
      setGlSaveError('Tenant context not found.');
      showError('Tenant context not found.');
      return;
    }
    setGlSaving(true);
    setGlSaveError(null);
    try {
      await startGlTabPostgresSync();
    } catch (apiError) {
      setGlSaveError(apiError.message || 'Failed to start sync');
      showError(apiError.message || 'Failed to start sync');
    } finally {
      setGlSaving(false);
    }
  };

  // —— Chart of Accounts: background sync Snowflake → Postgres (FastAPI) — same params as server filter example
  const startChartPostgresSync = async () => {
    if (chartPostgresSyncStarting) return;
    if (!localStorage.getItem('tenant_integration') || !localStorage.getItem('tenant_id')) {
      showError('Tenant context is required (tenant_id + tenant_integration in local storage).');
      return;
    }
    stopChartPostgresPoll();
    setChartPostgresJobError(null);
    setChartPostgresJobStatus(null);
    setChartPostgresSyncStarting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const url = buildCoaPostgresSyncStartUrl();
      const resp = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.message || data.detail || `Start failed (${resp.status})`);
      }
      const jobId = data.job_id;
      if (!jobId) throw new Error('No job_id returned from server.');

      let chartTabCompletionToastSent = false;
      setChartPostgresPolling(true);

      const runOnce = async () => {
        const stUrl = buildCoaPostgresSyncStatusUrl(jobId);
        const stResp = await fetch(stUrl, { headers: { Authorization: `Bearer ${token}` } });
        const st = await stResp.json().catch(() => ({}));
        if (!stResp.ok) {
          stopChartPostgresPoll();
          setChartPostgresJobError(postgresSyncFailureText(st, `Status ${stResp.status}`));
          return true;
        }
        setChartPostgresJobStatus(st);
        const s = getPostgresSyncPollStatusKey(st);
        if (s === 'success' || s === 'completed' || s === 'failed' || s === 'error' || s === 'cancelled') {
          stopChartPostgresPoll();
          if (s === 'success' || s === 'completed') {
            if (!chartTabCompletionToastSent) {
              chartTabCompletionToastSent = true;
              showToast({
                title: 'Chart of accounts → Postgres sync complete',
                message: formatPostgresSyncJobCompleteMessage(st, 'Chart of accounts'),
                severity: 'success',
                autoHideDuration: POSTGRES_JOB_SUCCESS_TOAST_MS,
              });
            }
            setChartData([]);
          } else {
            const chartTabFail = postgresSyncFailureText(
              st,
              'Chart of accounts → Postgres sync did not complete successfully.',
            );
            setChartPostgresJobError(chartTabFail);
            showError(chartTabFail);
          }
          return true;
        }
        return false;
      };

      const firstDone = await runOnce();
      if (!firstDone) {
        chartPostgresPollRef.current = setInterval(() => {
          void runOnce();
        }, 2000);
      }
    } catch (e) {
      setChartPostgresJobError(e.message);
      showError(e.message);
      stopChartPostgresPoll();
    } finally {
      setChartPostgresSyncStarting(false);
    }
  };

  const handleSaveChartRefreshData = async () => {
    if (!localStorage.getItem('tenant_id') || !localStorage.getItem('tenant_integration')) {
      setChartError('Tenant context not found.');
      showError('Tenant context not found.');
      return;
    }
    setChartSaving(true);
    setChartError(null);
    try {
      await startChartPostgresSync();
    } catch (apiError) {
      setChartError(apiError.message || 'Failed to start sync');
      showError(apiError.message || 'Failed to start sync');
    } finally {
      setChartSaving(false);
    }
  };

  const getPgPropertyName = () => {
    const p = propertyApis.find((q) => q.propertyId === pgPropertyId);
    return propertyDisplayNameForApi(p);
  };

  const startBankPostgresSync = async () => {
    if (pgBankSyncStarting) return;
    if (!localStorage.getItem('tenant_integration') || !localStorage.getItem('tenant_id')) {
      showError('Tenant context is required (tenant_id + tenant_integration in local storage).');
      return;
    }
    stopPostgresBankPoll();
    setPgBankJobError(null);
    setPgBankJobStatus(null);
    setPgBankSyncStarting(true);
    const propName = getPgPropertyName();
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const url = buildPostgresSyncStartUrl('bank-transactions', {
        startDate: pgFromDate,
        endDate: pgToDate,
        propertyName: propName || undefined,
      });
      const resp = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.message || data.detail || `Start failed (${resp.status})`);
      }
      const jobId = data.job_id;
      if (!jobId) throw new Error('No job_id returned from server.');

      let pgBankCompletionToastSent = false;
      setPgBankPolling(true);

      const runOnce = async () => {
        const stUrl = buildPostgresSyncStatusUrl('bank-transactions', jobId);
        const stResp = await fetch(stUrl, { headers: { Authorization: `Bearer ${token}` } });
        const st = await stResp.json().catch(() => ({}));
        if (!stResp.ok) {
          stopPostgresBankPoll();
          setPgBankJobError(postgresSyncFailureText(st, `Status ${stResp.status}`));
          return true;
        }
        setPgBankJobStatus(st);
        const s = getPostgresSyncPollStatusKey(st);
        if (s === 'success' || s === 'completed' || s === 'failed' || s === 'error' || s === 'cancelled') {
          stopPostgresBankPoll();
          if (s === 'success' || s === 'completed') {
            if (!pgBankCompletionToastSent) {
              pgBankCompletionToastSent = true;
              showToast({
                title: 'Bank → Postgres sync complete',
                message: formatPostgresSyncJobCompleteMessage(st, 'Bank'),
                severity: 'success',
                autoHideDuration: POSTGRES_JOB_SUCCESS_TOAST_MS,
              });
            }
          } else {
            const pgBankFail = postgresSyncFailureText(st, 'Bank → Postgres sync did not complete successfully.');
            setPgBankJobError(pgBankFail);
            showError(pgBankFail);
          }
          return true;
        }
        return false;
      };

      const firstDone = await runOnce();
      if (!firstDone) {
        bankPollRef.current = setInterval(() => {
          void runOnce();
        }, 2000);
      }
    } catch (e) {
      setPgBankJobError(e.message);
      showError(e.message);
      stopPostgresBankPoll();
    } finally {
      setPgBankSyncStarting(false);
    }
  };

  const startGlPostgresSync = async () => {
    if (pgGlSyncStarting) return;
    if (!localStorage.getItem('tenant_integration') || !localStorage.getItem('tenant_id')) {
      showError('Tenant context is required (tenant_id + tenant_integration in local storage).');
      return;
    }
    stopPostgresGlPoll();
    setPgGlJobError(null);
    setPgGlJobStatus(null);
    setPgGlSyncStarting(true);
    const propName = getPgPropertyName();
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const url = buildPostgresSyncStartUrl('gl-transactions', {
        startDate: pgFromDate,
        endDate: pgToDate,
        propertyName: propName || undefined,
      });
      const resp = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.message || data.detail || `Start failed (${resp.status})`);
      }
      const jobId = data.job_id;
      if (!jobId) throw new Error('No job_id returned from server.');

      let pgGlCompletionToastSent = false;
      setPgGlPolling(true);

      const runOnce = async () => {
        const stUrl = buildPostgresSyncStatusUrl('gl-transactions', jobId);
        const stResp = await fetch(stUrl, { headers: { Authorization: `Bearer ${token}` } });
        const st = await stResp.json().catch(() => ({}));
        if (!stResp.ok) {
          stopPostgresGlPoll();
          setPgGlJobError(postgresSyncFailureText(st, `Status ${stResp.status}`));
          return true;
        }
        setPgGlJobStatus(st);
        const s = getPostgresSyncPollStatusKey(st);
        if (s === 'success' || s === 'completed' || s === 'failed' || s === 'error' || s === 'cancelled') {
          stopPostgresGlPoll();
          if (s === 'success' || s === 'completed') {
            if (!pgGlCompletionToastSent) {
              pgGlCompletionToastSent = true;
              showToast({
                title: 'GL → Postgres sync complete',
                message: formatPostgresSyncJobCompleteMessage(st, 'GL'),
                severity: 'success',
                autoHideDuration: POSTGRES_JOB_SUCCESS_TOAST_MS,
              });
            }
          } else {
            const pgGlFail = postgresSyncFailureText(st, 'GL → Postgres sync did not complete successfully.');
            setPgGlJobError(pgGlFail);
            showError(pgGlFail);
          }
          return true;
        }
        return false;
      };

      const firstDone = await runOnce();
      if (!firstDone) {
        glPollRef.current = setInterval(() => {
          void runOnce();
        }, 2000);
      }
    } catch (e) {
      setPgGlJobError(e.message);
      showError(e.message);
      stopPostgresGlPoll();
    } finally {
      setPgGlSyncStarting(false);
    }
  };

  /** Chart of accounts → Postgres for the shared tab (same FastAPI contract as the Chart tab). */
  const startPgChartPostgresSync = async () => {
    if (pgChartSyncStarting) return;
    if (!localStorage.getItem('tenant_integration') || !localStorage.getItem('tenant_id')) {
      showError('Tenant context is required (tenant_id + tenant_integration in local storage).');
      return;
    }
    stopPgChartPoll();
    setPgChartJobError(null);
    setPgChartJobStatus(null);
    setPgChartSyncStarting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const url = buildCoaPostgresSyncStartUrl();
      const resp = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.message || data.detail || `Start failed (${resp.status})`);
      }
      const jobId = data.job_id;
      if (!jobId) throw new Error('No job_id returned from server.');

      let pgChartCompletionToastSent = false;
      setPgChartPolling(true);

      const runOnce = async () => {
        const stUrl = buildCoaPostgresSyncStatusUrl(jobId);
        const stResp = await fetch(stUrl, { headers: { Authorization: `Bearer ${token}` } });
        const st = await stResp.json().catch(() => ({}));
        if (!stResp.ok) {
          stopPgChartPoll();
          setPgChartJobError(postgresSyncFailureText(st, `Status ${stResp.status}`));
          return true;
        }
        setPgChartJobStatus(st);
        const s = getPostgresSyncPollStatusKey(st);
        if (s === 'success' || s === 'completed' || s === 'failed' || s === 'error' || s === 'cancelled') {
          stopPgChartPoll();
          if (s === 'success' || s === 'completed') {
            if (!pgChartCompletionToastSent) {
              pgChartCompletionToastSent = true;
              showToast({
                title: 'Chart of accounts → Postgres sync complete',
                message: formatPostgresSyncJobCompleteMessage(st, 'Chart of accounts'),
                severity: 'success',
                autoHideDuration: POSTGRES_JOB_SUCCESS_TOAST_MS,
              });
            }
          } else {
            const pgChartFail = postgresSyncFailureText(
              st,
              'Chart of accounts → Postgres sync did not complete successfully.',
            );
            setPgChartJobError(pgChartFail);
            showError(pgChartFail);
          }
          return true;
        }
        return false;
      };

      const firstDone = await runOnce();
      if (!firstDone) {
        pgChartPollRef.current = setInterval(() => {
          void runOnce();
        }, 2000);
      }
    } catch (e) {
      setPgChartJobError(e.message);
      showError(e.message);
      stopPgChartPoll();
    } finally {
      setPgChartSyncStarting(false);
    }
  };

  /** Footer "Load" starts bank, GL, and chart `sync-to-postgres` jobs (bank/GL share property/date filters; chart uses ledger scope). */
  const handleLoadPostgresBoth = () => {
    if (!localStorage.getItem('tenant_integration') || !localStorage.getItem('tenant_id')) {
      showError('Tenant context is required (tenant_id + tenant_integration in local storage).');
      return;
    }
    if (
      pgBankSyncStarting ||
      pgGlSyncStarting ||
      pgChartSyncStarting ||
      pgBankPolling ||
      pgGlPolling ||
      pgChartPolling
    ) {
      return;
    }
    combinedLoadRequestedRef.current = true;
    void Promise.allSettled([startBankPostgresSync(), startGlPostgresSync(), startPgChartPostgresSync()]);
  };

  /** Leave the three Complete lines on screen briefly, then close the dialog. */
  const COMBINED_REFRESH_CLOSE_DELAY_MS = 2000;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open || !combinedLoadRequestedRef.current) return undefined;
    if (pgBankSyncStarting || pgGlSyncStarting || pgChartSyncStarting) return undefined;
    if (pgBankPolling || pgGlPolling || pgChartPolling) return undefined;
    const bankDone = POSTGRES_SYNC_DONE.has(getPostgresSyncPollStatusKey(pgBankJobStatus));
    const glDone = POSTGRES_SYNC_DONE.has(getPostgresSyncPollStatusKey(pgGlJobStatus));
    const chartDone = POSTGRES_SYNC_DONE.has(getPostgresSyncPollStatusKey(pgChartJobStatus));
    if (!bankDone || !glDone || !chartDone) return undefined;
    const id = window.setTimeout(() => {
      if (!combinedLoadRequestedRef.current) return;
      combinedLoadRequestedRef.current = false;
      resetOnClose();
      onCloseRef.current?.();
    }, COMBINED_REFRESH_CLOSE_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [
    open,
    pgBankJobStatus,
    pgGlJobStatus,
    pgChartJobStatus,
    pgBankSyncStarting,
    pgGlSyncStarting,
    pgChartSyncStarting,
    pgBankPolling,
    pgGlPolling,
    pgChartPolling,
  ]);

  const bankColumns = [
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
    { field: 'date', headerName: 'Date', width: 120 },
    { field: 'docNo', headerName: 'Doc No', width: 150 },
    {
      field: 'debit',
      headerName: 'Debit',
      width: 120,
      type: 'number',
      valueFormatter: (params) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value || 0),
    },
    {
      field: 'credit',
      headerName: 'Credit',
      width: 120,
      type: 'number',
      valueFormatter: (params) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value || 0),
    },
  ];

  const chartColumns = [
    { field: 'glSystem', headerName: 'GL System', width: 140 },
    { field: 'glCode', headerName: 'GL Code', width: 120 },
    { field: 'glDescription', headerName: 'GL Description', flex: 1, minWidth: 220 },
    { field: 'glAccount', headerName: 'GL Account', flex: 1, minWidth: 200 },
    { field: 'glKey', headerName: 'GL Key', width: 100 },
    { field: 'glAccountType', headerName: 'Account Type', width: 140 },
    { field: 'glAlternativeAccount', headerName: 'Alternative GL Account', width: 200 },
    { field: 'glCategory', headerName: 'Category', width: 200 },
    { field: 'glClosingType', headerName: 'Closing Type', width: 150 },
    { field: 'glCloseToAccountId', headerName: 'Close To Account Id', width: 170 },
    { field: 'glCloseToAccountKey', headerName: 'Close To Account Key', width: 190 },
    { field: 'glNormalBalance', headerName: 'Normal Balance', width: 150 },
    { field: 'glStatus', headerName: 'Status', width: 120 },
    { field: 'glDisallowDirectPosting', headerName: 'Disallow Direct Posting', width: 200 },
    { field: 'glIsTaxable', headerName: 'Is Taxable', width: 120 },
    { field: 'glAuditCreated', headerName: 'Created At', width: 190 },
    { field: 'glAuditModified', headerName: 'Modified At', width: 190 },
    { field: 'glEntityId', headerName: 'Entity Id', width: 140 },
    { field: 'glEntityKey', headerName: 'Entity Key', width: 140 },
    { field: 'glEntityName', headerName: 'Entity Name', width: 200 },
    { field: 'glMrcCode', headerName: 'MRC Code', width: 120 },
    { field: 'glTaxCode', headerName: 'Tax Code', width: 140 },
  ];

  const glColumns = [
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
    { field: 'date', headerName: 'Date', width: 120 },
    { field: 'docNo', headerName: 'Doc-No', width: 150 },
    {
      field: 'debit',
      headerName: 'Debit',
      width: 120,
      type: 'number',
      valueFormatter: (params) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(params.value || 0),
    },
    {
      field: 'credit',
      headerName: 'Credit',
      width: 120,
      type: 'number',
      valueFormatter: (params) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(params.value || 0),
    },
    { field: 'remarks', headerName: 'Remarks', flex: 1, minWidth: 200 },
  ];

  const pgBankCountReady = !pgApiBankCountLoading && typeof pgApiBankCount === 'number';
  const pgGlCountReady = !pgApiGlCountLoading && typeof pgApiGlCount === 'number';
  const pgChartCountReady = !pgApiChartCountLoading && typeof pgApiChartCount === 'number';
  const pgCombinedRecordTotal =
    pgBankCountReady && pgGlCountReady && pgChartCountReady
      ? pgApiBankCount + pgApiGlCount + pgApiChartCount
      : null;

  const tabPanelValue = showIndividualRefreshTabs ? activeTab : 3;

  return (
    <Dialog
      open={open}
      onClose={syncInProgress ? () => {} : handleClose}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown={syncInProgress}
      PaperProps={harmonateDialogPaperProps}
    >
      {syncInProgress && (
        <LinearProgress
          sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1300 }}
          color="primary"
        />
      )}
      <DialogTitle
        sx={{
          ...harmonateDialogTitleSx,
          fontSize: '1rem',
        }}
      >
        <span>Refresh Data</span>
        {!showIndividualRefreshTabs && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowIndividualRefreshTabs(true)}
            sx={{
              flexShrink: 0,
              textTransform: 'none',
              borderRadius: ht.buttonRadius,
              borderColor: 'rgba(255, 255, 255, 0.7)',
              color: ht.white,
              bgcolor: 'rgba(255, 255, 255, 0.08)',
              fontSize: '0.875rem',
              fontWeight: 500,
              px: 2,
              '&:hover': {
                borderColor: ht.white,
                bgcolor: 'rgba(255, 255, 255, 0.22)',
                color: ht.white,
              },
            }}
          >
            GL, Bank, Chart
          </Button>
        )}
      </DialogTitle>
      <DialogContent sx={{ ...harmonateDialogContentSx, ...harmonateFormFieldsSx }}>
        {showIndividualRefreshTabs && (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
                columnGap: 2,
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(e, v) => setActiveTab(v)}
                aria-label="Refresh Data tabs"
                variant="scrollable"
                scrollButtons="auto"
                sx={{ ...harmonateTabsSx, flex: 1, minWidth: 0 }}
              >
                <Tab label="GL Refresh Data" {...a11yProps(0)} />
                <Tab label="Bank Refresh Data" {...a11yProps(1)} />
                <Tab label="Chart of Accounts Refresh Data" {...a11yProps(2)} />
                <Tab label="Bank & GL" {...a11yProps(3)} />
              </Tabs>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setShowIndividualRefreshTabs(false);
                  setActiveTab(3);
                }}
                sx={{
                  textTransform: 'none',
                  flexShrink: 0,
                  color: ht.green,
                  fontWeight: 500,
                  '&:hover': {
                    bgcolor: 'rgba(2, 107, 29, 0.08)',
                    color: ht.green,
                  },
                }}
              >
                Combined only
              </Button>
            </Box>
          </Box>
        )}

        {/* GL Refresh Data tab */}
        <CustomTabPanel value={tabPanelValue} index={0}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={4}>
              {loadingPropertyApis ? (
                <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={{ ml: 1 }}>Loading Properties...</Typography>
                </Box>
              ) : (
                <Autocomplete
                  fullWidth
                  options={propertyApis}
                  getOptionLabel={(option) => {
                    const code = option.propertyCode || '';
                    const name = option.propertyName || '';
                    return code && name ? `${code} - ${name}` : name;
                  }}
                  value={propertyApis.find((p) => p.propertyId === glPropertyId) || null}
                  onChange={(e, newValue) => setGlPropertyId(newValue ? newValue.propertyId : '')}
                  renderInput={(params) => (
                    <TextField {...params} label="Property" size="small" placeholder="Select Property" margin="normal" />
                  )}
                  isOptionEqualToValue={(option, value) => option.propertyId === value?.propertyId}
                />
              )}
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="From Date"
                type="date"
                value={glFromDate}
                onChange={(e) => setGlFromDate(e.target.value)}
                size="small"
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="To Date"
                type="date"
                value={glToDate}
                onChange={(e) => setGlToDate(e.target.value)}
                size="small"
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant="contained"
              onClick={() => fetchGLRefreshData(false)}
              disabled={
                !localStorage.getItem('tenant_integration') ||
                glLoading ||
                glSaving ||
                glTabPostgresSyncStarting ||
                glTabPostgresPolling
              }
              sx={harmonateProceedButtonSx}
            >
              {glLoading && <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} />}
              Preview
            </Button>
            {glData.length > 0 && glHasMore && (
              <Button
                variant="outlined"
                onClick={() => fetchGLRefreshData(true)}
                disabled={
                  !localStorage.getItem('tenant_integration') ||
                  glLoading ||
                  glSaving ||
                  glTabPostgresSyncStarting ||
                  glTabPostgresPolling
                }
                sx={{ ...harmonateUploadOutlinedSx, fontSize: '14px' }}
              >
                {glLoading ? 'Loading...' : 'Load next 1000'}
              </Button>
            )}
          </Box>
          {glTabPostgresJobError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGlTabPostgresJobError(null)}>
              {glTabPostgresJobError}
            </Alert>
          )}
          {(glTabPostgresPolling || glTabPostgresJobStatus) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                GL → Postgres: <PostgresSyncStatusLabel job={glTabPostgresJobStatus} />
                {glTabPostgresJobStatus?.job_id != null && ` · ${String(glTabPostgresJobStatus.job_id).slice(0, 8)}…`}
                {glTabPostgresJobStatus?.job_type != null && ` · ${glTabPostgresJobStatus.job_type}`}
                {glTabPostgresJobStatus?.processed_rows != null &&
                  ` · Processed: ${glTabPostgresJobStatus.processed_rows}`}
                {glTabPostgresJobStatus?.upserted_rows != null &&
                  ` · Upserted: ${glTabPostgresJobStatus.upserted_rows}`}
                {glTabPostgresJobStatus?.estimated_total_rows != null &&
                  ` / est. ${glTabPostgresJobStatus.estimated_total_rows}`}
                {glTabPostgresJobStatus?.eta_seconds != null && glTabPostgresJobStatus.eta_seconds > 0 &&
                  ` · ETA ~${Number(glTabPostgresJobStatus.eta_seconds).toFixed(0)}s`}
              </Typography>
              <PostgresSyncJobProgress job={glTabPostgresJobStatus} />
            </Box>
          )}
          {(glError || glSaveError) && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setGlError(null); setGlSaveError(null); }}>
              {glError || glSaveError}
            </Alert>
          )}
          <Box sx={{ height: REFRESH_DATA_GRID_HEIGHT, flexShrink: 0 }}>
            {glLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>Loading data...</Typography>
              </Box>
            ) : (
              <DataGridPro
                rows={glData}
                columns={glColumns}
                rowHeight={40}
                getRowId={(row) => row.id}
                sx={{ ...harmonateDataGridSx, border: 'none', '& .MuiDataGrid-columnSeparator': { display: 'none' } }}
                slots={{
                  noRowsOverlay: () => (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                      <Typography>No data. Use Preview (optional: property, dates).</Typography>
                    </Box>
                  ),
                }}
                pagination
                pageSizeOptions={[10, 25, 50]}
                pageSize={25}
              />
            )}
          </Box>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Total records :
              {glApiTotalLoading ? (
                <CircularProgress size={16} thickness={5} />
              ) : (
                glApiTotalCount ?? '—'
              )}
            </Typography>
          </Box>
        </CustomTabPanel>

        {/* Bank Refresh Data tab */}
        <CustomTabPanel value={tabPanelValue} index={1}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={4}>
              {loadingPropertyApis ? (
                <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={{ ml: 1 }}>Loading Properties...</Typography>
                </Box>
              ) : (
                <Autocomplete
                  fullWidth
                  options={propertyApis}
                  getOptionLabel={(option) => {
                    const code = option.propertyCode || '';
                    const name = option.propertyName || '';
                    return code && name ? `${code} - ${name}` : name;
                  }}
                  value={propertyApis.find((p) => p.propertyId === bankPropertyId) || null}
                  onChange={(e, newValue) => setBankPropertyId(newValue ? newValue.propertyId : '')}
                  renderInput={(params) => (
                    <TextField {...params} label="Property" size="small" placeholder="Select Property" margin="normal" />
                  )}
                  isOptionEqualToValue={(option, value) => option.propertyId === value?.propertyId}
                />
              )}
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="From Date"
                type="date"
                value={bankFromDate}
                onChange={(e) => setBankFromDate(e.target.value)}
                size="small"
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="To Date"
                type="date"
                value={bankToDate}
                onChange={(e) => setBankToDate(e.target.value)}
                size="small"
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant="contained"
              onClick={() => fetchBankRefreshData(false)}
              disabled={
                !localStorage.getItem('tenant_integration') ||
                bankLoading ||
                bankSaving ||
                bankTabPostgresSyncStarting ||
                bankTabPostgresPolling
              }
              sx={harmonateProceedButtonSx}
            >
              {bankLoading && <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} />}
              Preview
            </Button>
            {bankData.length > 0 && bankHasMore && (
              <Button
                variant="outlined"
                onClick={() => fetchBankRefreshData(true)}
                disabled={
                  !localStorage.getItem('tenant_integration') ||
                  bankLoading ||
                  bankSaving ||
                  bankTabPostgresSyncStarting ||
                  bankTabPostgresPolling
                }
                sx={{ ...harmonateUploadOutlinedSx, fontSize: '14px' }}
              >
                {bankLoading ? 'Loading...' : 'Load next 1000'}
              </Button>
            )}
          </Box>
          {bankTabPostgresJobError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBankTabPostgresJobError(null)}>
              {bankTabPostgresJobError}
            </Alert>
          )}
          {(bankTabPostgresPolling || bankTabPostgresJobStatus) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Bank → Postgres: <PostgresSyncStatusLabel job={bankTabPostgresJobStatus} />
                {bankTabPostgresJobStatus?.job_id != null && ` · ${String(bankTabPostgresJobStatus.job_id).slice(0, 8)}…`}
                {bankTabPostgresJobStatus?.job_type != null && ` · ${bankTabPostgresJobStatus.job_type}`}
                {bankTabPostgresJobStatus?.processed_rows != null &&
                  ` · Processed: ${bankTabPostgresJobStatus.processed_rows}`}
                {bankTabPostgresJobStatus?.upserted_rows != null &&
                  ` · Upserted: ${bankTabPostgresJobStatus.upserted_rows}`}
                {bankTabPostgresJobStatus?.estimated_total_rows != null &&
                  ` / est. ${bankTabPostgresJobStatus.estimated_total_rows}`}
                {bankTabPostgresJobStatus?.eta_seconds != null && bankTabPostgresJobStatus.eta_seconds > 0 &&
                  ` · ETA ~${Number(bankTabPostgresJobStatus.eta_seconds).toFixed(0)}s`}
              </Typography>
              <PostgresSyncJobProgress job={bankTabPostgresJobStatus} />
            </Box>
          )}
          {(bankError || bankSaveError) && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setBankError(null); setBankSaveError(null); }}>
              {bankError || bankSaveError}
            </Alert>
          )}
          <Box sx={{ height: REFRESH_DATA_GRID_HEIGHT, flexShrink: 0 }}>
            {bankLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>Loading data...</Typography>
              </Box>
            ) : (
              <DataGridPro
                rows={bankData}
                columns={bankColumns}
                rowHeight={40}
                getRowId={(row) => row.id}
                sx={{ ...harmonateDataGridSx, border: 'none', '& .MuiDataGrid-columnSeparator': { display: 'none' } }}
                slots={{
                  noRowsOverlay: () => (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                      <Typography>No data. Use Preview (optional: property, dates).</Typography>
                    </Box>
                  ),
                }}
                pagination
                pageSizeOptions={[10, 25, 50]}
                pageSize={25}
              />
            )}
          </Box>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Total records :
              {bankApiTotalLoading ? (
                <CircularProgress size={16} thickness={5} />
              ) : (
                bankApiTotalCount ?? '—'
              )}
            </Typography>
          </Box>
        </CustomTabPanel>

        {/* Chart of Accounts Refresh Data tab */}
        <CustomTabPanel value={tabPanelValue} index={2}>
          
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant="contained"
              onClick={fetchChartRefreshData}
              disabled={
                !localStorage.getItem('tenant_integration') ||
                chartLoading ||
                chartSaving ||
                chartPostgresSyncStarting ||
                chartPostgresPolling
              }
              sx={harmonateProceedButtonSx}
            >
              {chartLoading && <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} />}
              Preview
            </Button>
          </Box>
          {chartPostgresJobError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setChartPostgresJobError(null)}>
              {chartPostgresJobError}
            </Alert>
          )}
          {chartError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setChartError(null)}>
              {chartError}
            </Alert>
          )}
          {(chartPostgresPolling || chartPostgresJobStatus) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                CoA → Postgres: <PostgresSyncStatusLabel job={chartPostgresJobStatus} />
                {chartPostgresJobStatus?.job_id != null && ` · ${String(chartPostgresJobStatus.job_id).slice(0, 8)}…`}
                {chartPostgresJobStatus?.job_type != null && ` · ${chartPostgresJobStatus.job_type}`}
                {chartPostgresJobStatus?.processed_rows != null &&
                  ` · Processed: ${chartPostgresJobStatus.processed_rows}`}
                {chartPostgresJobStatus?.upserted_rows != null &&
                  ` · Upserted: ${chartPostgresJobStatus.upserted_rows}`}
                {chartPostgresJobStatus?.estimated_total_rows != null &&
                  ` / est. ${chartPostgresJobStatus.estimated_total_rows}`}
                {chartPostgresJobStatus?.eta_seconds != null && chartPostgresJobStatus.eta_seconds > 0 &&
                  ` · ETA ~${Number(chartPostgresJobStatus.eta_seconds).toFixed(0)}s`}
              </Typography>
              <PostgresSyncJobProgress job={chartPostgresJobStatus} />
            </Box>
          )}
          <Box sx={{ height: REFRESH_DATA_GRID_HEIGHT, flexShrink: 0 }}>
            {chartLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>Loading data...</Typography>
              </Box>
            ) : (
              <DataGridPro
                rows={chartData}
                columns={chartColumns}
                rowHeight={40}
                getRowId={(row) => row.id}
                sx={{ ...harmonateDataGridSx, border: 'none', '& .MuiDataGrid-columnSeparator': { display: 'none' } }}
                pagination
                pageSizeOptions={[10, 25, 50]}
                pageSize={25}
              />
            )}
          </Box>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Total records :
              {chartApiTotalLoading ? (
                <CircularProgress size={16} thickness={5} />
              ) : (
                chartApiTotalCount ?? '—'
              )}
            </Typography>
          </Box>
        </CustomTabPanel>

        {/* Bank & GL (Postgres): filters + totals + job progress; footer Load starts bank, GL, and chart syncs */}
        <CustomTabPanel value={tabPanelValue} index={3}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={4}>
              {loadingPropertyApis ? (
                <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={{ ml: 1 }}>Loading Properties...</Typography>
                </Box>
              ) : (
                <Autocomplete
                  fullWidth
                  options={propertyApis}
                  getOptionLabel={(option) => {
                    const code = option.propertyCode || '';
                    const name = option.propertyName || '';
                    return code && name ? `${code} - ${name}` : name;
                  }}
                  value={propertyApis.find((p) => p.propertyId === pgPropertyId) || null}
                  onChange={(e, newValue) => setPgPropertyId(newValue ? newValue.propertyId : '')}
                  renderInput={(params) => (
                    <TextField {...params} label="Property" size="small" placeholder="Select Property" margin="normal" />
                  )}
                  isOptionEqualToValue={(option, value) => option.propertyId === value?.propertyId}
                />
              )}
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="From Date"
                type="date"
                value={pgFromDate}
                onChange={(e) => setPgFromDate(e.target.value)}
                size="small"
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="To Date"
                type="date"
                value={pgToDate}
                onChange={(e) => setPgToDate(e.target.value)}
                size="small"
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 1,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              Record totals
            </Typography>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Bank
                </Typography>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {pgApiBankCountLoading ? <CircularProgress size={22} thickness={5} /> : (pgApiBankCount ?? '—')}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  GL
                </Typography>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {pgApiGlCountLoading ? <CircularProgress size={22} thickness={5} /> : (pgApiGlCount ?? '—')}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Chart
                </Typography>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {pgApiChartCountLoading ? <CircularProgress size={22} thickness={5} /> : (pgApiChartCount ?? '—')}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Combined
                </Typography>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {pgApiBankCountLoading || pgApiGlCountLoading || pgApiChartCountLoading ? (
                    <CircularProgress size={22} thickness={5} />
                  ) : (
                    pgCombinedRecordTotal ?? '—'
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {pgBankJobError && (
            <Alert severity="error" sx={{ mb: 1 }} onClose={() => setPgBankJobError(null)}>
              {pgBankJobError}
            </Alert>
          )}
          {(pgBankPolling || pgBankJobStatus) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Bank job: <PostgresSyncStatusLabel job={pgBankJobStatus} />
                {pgBankJobStatus?.job_id != null && ` · ${String(pgBankJobStatus.job_id).slice(0, 8)}…`}
                {pgBankJobStatus?.processed_rows != null &&
                  ` · Processed: ${pgBankJobStatus.processed_rows}`}
                {pgBankJobStatus?.upserted_rows != null &&
                  ` · Upserted: ${pgBankJobStatus.upserted_rows}`}
                {pgBankJobStatus?.estimated_total_rows != null &&
                  ` / est. ${pgBankJobStatus.estimated_total_rows}`}
                {pgBankJobStatus?.eta_seconds != null && pgBankJobStatus.eta_seconds > 0 &&
                  ` · ETA ~${Number(pgBankJobStatus.eta_seconds).toFixed(0)}s`}
              </Typography>
              <PostgresSyncJobProgress job={pgBankJobStatus} />
            </Box>
          )}

          {pgGlJobError && (
            <Alert severity="error" sx={{ mb: 1 }} onClose={() => setPgGlJobError(null)}>
              {pgGlJobError}
            </Alert>
          )}
          {(pgGlPolling || pgGlJobStatus) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                GL job: <PostgresSyncStatusLabel job={pgGlJobStatus} />
                {pgGlJobStatus?.job_id != null && ` · ${String(pgGlJobStatus.job_id).slice(0, 8)}…`}
                {pgGlJobStatus?.processed_rows != null && ` · Processed: ${pgGlJobStatus.processed_rows}`}
                {pgGlJobStatus?.upserted_rows != null && ` · Upserted: ${pgGlJobStatus.upserted_rows}`}
                {pgGlJobStatus?.estimated_total_rows != null && ` / est. ${pgGlJobStatus.estimated_total_rows}`}
                {pgGlJobStatus?.eta_seconds != null && pgGlJobStatus.eta_seconds > 0 &&
                  ` · ETA ~${Number(pgGlJobStatus.eta_seconds).toFixed(0)}s`}
              </Typography>
              <PostgresSyncJobProgress job={pgGlJobStatus} />
            </Box>
          )}

          {pgChartJobError && (
            <Alert severity="error" sx={{ mb: 1 }} onClose={() => setPgChartJobError(null)}>
              {pgChartJobError}
            </Alert>
          )}
          {(pgChartPolling || pgChartJobStatus) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Chart of accounts job: <PostgresSyncStatusLabel job={pgChartJobStatus} />
                {pgChartJobStatus?.job_id != null && ` · ${String(pgChartJobStatus.job_id).slice(0, 8)}…`}
                {pgChartJobStatus?.processed_rows != null && ` · Processed: ${pgChartJobStatus.processed_rows}`}
                {pgChartJobStatus?.upserted_rows != null && ` · Upserted: ${pgChartJobStatus.upserted_rows}`}
                {pgChartJobStatus?.estimated_total_rows != null && ` / est. ${pgChartJobStatus.estimated_total_rows}`}
                {pgChartJobStatus?.eta_seconds != null && pgChartJobStatus.eta_seconds > 0 &&
                  ` · ETA ~${Number(pgChartJobStatus.eta_seconds).toFixed(0)}s`}
              </Typography>
              <PostgresSyncJobProgress job={pgChartJobStatus} />
            </Box>
          )}
        </CustomTabPanel>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={syncInProgress} variant="outlined" sx={harmonateCancelButtonSx}>
          Cancel
        </Button>
        {activeTab === 0 && (
          <Button
            onClick={() => handleOpenConfirmLoad('GL')}
            variant="contained"
            disabled={
              glSaving ||
              confirmLoadSubmitting ||
              glTabPostgresSyncStarting ||
              glTabPostgresPolling
            }
            sx={harmonateProceedButtonSx}
          >
            {(glSaving || glTabPostgresSyncStarting) && <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />}
            {glTabPostgresSyncStarting ? 'Starting…' : glSaving ? 'Syncing…' : 'Load'}
          </Button>
        )}
        {activeTab === 1 && (
          <Button
            onClick={() => handleOpenConfirmLoad('BANK')}
            variant="contained"
            disabled={
              bankSaving ||
              confirmLoadSubmitting ||
              bankTabPostgresSyncStarting ||
              bankTabPostgresPolling
            }
            sx={harmonateProceedButtonSx}
          >
            {(bankSaving || bankTabPostgresSyncStarting) && <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />}
            {bankTabPostgresSyncStarting ? 'Starting…' : bankSaving ? 'Syncing…' : 'Load'}
          </Button>
        )}
        {activeTab === 2 && (
          <Button
            onClick={() => handleOpenConfirmLoad('CHART')}
            variant="contained"
            disabled={
              chartSaving ||
              confirmLoadSubmitting ||
              chartPostgresSyncStarting ||
              chartPostgresPolling
            }
            sx={harmonateProceedButtonSx}
          >
            {(chartSaving || chartPostgresSyncStarting) && <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />}
            {chartPostgresSyncStarting ? 'Starting…' : chartSaving ? 'Syncing…' : 'Load'}
          </Button>
        )}
        {activeTab === 3 && (
          <Button
            onClick={handleLoadPostgresBoth}
            variant="contained"
            disabled={
              pgBankSyncStarting ||
              pgGlSyncStarting ||
              pgChartSyncStarting ||
              pgBankPolling ||
              pgGlPolling ||
              pgChartPolling ||
              confirmLoadSubmitting
            }
            sx={harmonateProceedButtonSx}
          >
            {(pgBankSyncStarting || pgGlSyncStarting || pgChartSyncStarting) && (
              <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
            )}
            {pgBankSyncStarting || pgGlSyncStarting || pgChartSyncStarting ? 'Starting…' : 'Load'}
          </Button>
        )}
      </DialogActions>

      {/* Confirm Load Dialog */}
      <Dialog
        open={confirmLoadOpen}
        onClose={confirmLoadSubmitting ? () => {} : handleCancelConfirmLoad}
        disableEscapeKeyDown={confirmLoadSubmitting}
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          {confirmLoadSubmitting ? 'Loading' : 'Confirm Load'}
        </DialogTitle>
        <DialogContent
          sx={{
            ...harmonateDialogContentSx,
            position: 'relative',
            minWidth: 280,
            /* Tall min-height only while overlay is shown; otherwise the dialog fits the short confirm text */
            ...(confirmLoadSubmitting ? { minHeight: 220 } : {}),
          }}
        >
          {confirmLoadSubmitting && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                bgcolor: 'rgba(255,255,255,0.92)',
                borderRadius: 1,
                py: 3,
                px: 2,
              }}
            >
              <CircularProgress size={48} thickness={4} />
              <Typography variant="body2" align="center" fontWeight={600}>
                Starting sync…
              </Typography>
              <Typography variant="caption" color="text.secondary" align="center">
                You can close this dialog after it closes; the job continues in the background. Large GL syncs can take many minutes.
              </Typography>
            </Box>
          )}
          {!confirmLoadSubmitting && confirmCountLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, minHeight: 80 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Fetching record count…
              </Typography>
            </Box>
          )}
          {!confirmLoadSubmitting && !confirmCountLoading && confirmCountError && (
            <Box sx={{ py: 1 }}>
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setConfirmCountError(null)}>
                {confirmCountError}
              </Alert>
              <Button
                variant="outlined"
                size="small"
                onClick={() => fetchConfirmLoadCount(confirmLoadType)}
                sx={{ textTransform: 'none' }}
              >
                Retry count
              </Button>
            </Box>
          )}
          {!confirmLoadSubmitting && !confirmCountLoading && !confirmCountError && confirmLoadCount != null && (
            <DialogContentText component="div" sx={{ whiteSpace: 'normal', overflow: 'visible' }}>
              {(confirmLoadType === 'GL' || confirmLoadType === 'BANK' || confirmLoadType === 'CHART') &&
                `We will load about ${confirmLoadCount} matching record${confirmLoadCount === 1 ? '' : 's'} in the background. This may take a while; progress will appear on this screen. Continue?`}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirmLoad} disabled={confirmLoadSubmitting} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLoad}
            variant="contained"
            disabled={
              confirmLoadSubmitting ||
              confirmCountLoading ||
              !!confirmCountError ||
              confirmLoadCount == null
            }
            sx={{ ...harmonateProceedButtonSx, minWidth: 120 }}
          >
            {confirmLoadSubmitting ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Syncing…
              </>
            ) : (
              'Load'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default RefreshData;
